import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// A page can take over the sidebar with its own navigation, e.g. the chat list
// on Virtual Team Members. The page registers here and renders its navigation
// into `slot` through a portal; the sidebar adds a way back to the main menu.
export interface SidebarContextNav {
  // Route of the main menu item that leads to the page.
  path: string;
  label: string;
}

// Split in two so the page that owns the portal only re-renders when the slot
// changes, not every time the sidebar collapses.
interface SidebarSlotValue {
  slot: HTMLElement | null;
  setSlot: (slot: HTMLElement | null) => void;
  setContextNav: (nav: SidebarContextNav | null) => void;
}

interface SidebarStateValue {
  contextNav: SidebarContextNav | null;
  collapsed: boolean;
  // Closes the mobile drawer after the page navigated somewhere.
  onNavigate?: () => void;
}

const SidebarSlotContext = createContext<SidebarSlotValue | null>(null);
const SidebarStateContext = createContext<SidebarStateValue | null>(null);

export const SidebarNavProvider = ({
  collapsed,
  onNavigate,
  children,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  children: React.ReactNode;
}) => {
  const [contextNav, setContextNav] = useState<SidebarContextNav | null>(null);
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  const slotValue = useMemo(() => ({ slot, setSlot, setContextNav }), [slot]);
  const stateValue = useMemo(
    () => ({ contextNav, collapsed, onNavigate }),
    [contextNav, collapsed, onNavigate],
  );

  return (
    <SidebarSlotContext.Provider value={slotValue}>
      <SidebarStateContext.Provider value={stateValue}>
        {children}
      </SidebarStateContext.Provider>
    </SidebarSlotContext.Provider>
  );
};

export const useSidebarSlot = () => useContext(SidebarSlotContext);
export const useSidebarNavState = () => useContext(SidebarStateContext);

// Registers the page's navigation for as long as the page is mounted and
// returns the element to portal it into.
export const useSidebarContextNav = (nav: SidebarContextNav) => {
  const sidebarSlot = useContext(SidebarSlotContext);
  const setContextNav = sidebarSlot?.setContextNav;
  const { path, label } = nav;

  useEffect(() => {
    if (!setContextNav) return;
    setContextNav({ path, label });
    return () => setContextNav(null);
  }, [setContextNav, path, label]);

  return sidebarSlot?.slot ?? null;
};
